import React, { useState, useEffect, useContext, Fragment } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LoginIcon from "@mui/icons-material/Login";
import PublicIcon from "@mui/icons-material/Public";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { useLocation, Link as RouterLink, useNavigate } from "react-router-dom";
import { useTheme as useMuiTheme } from "@mui/material/styles";

import { UserContext } from "../auth/auth-context";
import PositionedMenu from "./accountMenu";
import RegionSelector from "./regionSelector";
import { StyledLink } from "../../assets/css/styledComponent";
import Logo from "../../assets/images/plany365.ico";
import { useTokenLoader } from "../../util/tokenLoader";
import { useIsMobile } from "../../util/utils";
import { useManageUrl } from "./hooks/useManageUrl";
import { PlanyBrandMark } from "./planyBrandMark";
import { APP_IDS, getRuntimeAppProfile } from "../../runtime/appProfile";

/** How far to push the LEFT group inward on sm+ to clear your hidden left tab */
const LEFT_HIDDEN_TAB_OFFSET = 64; // px — tweak as needed

const CustomerHeader = () => {
  const { providerUser, logout } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const loginHref = React.useMemo(
    () => `/login?returnTo=${encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)}`,
    [location.pathname, location.search, location.hash]
  );
  const tokenLoaderResult = useTokenLoader();

  // Robust normalization for tokenLoader values (array/object/boolean/number)
  const tokenLoading = React.useMemo(() => {
    const v = tokenLoaderResult;
    if (Array.isArray(v)) return Boolean(v[0]); // [loading, ...]
    if (v && typeof v === "object") {
      return Boolean(v.loading ?? v.isLoading ?? v.pending ?? false);
    }
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v > 0;
    return false;
  }, [tokenLoaderResult]);

  const theme = useMuiTheme();
  const chrome = theme.customChrome || {};
  const manageUrl = useManageUrl(providerUser);
  const isTimelyApp = React.useMemo(
    () => getRuntimeAppProfile().id === APP_IDS.TIMELY,
    []
  );

  useEffect(() => {
    const scroller = document.querySelector("#main");
    if (!scroller) return;
    const handleScroll = () => { };
    scroller.addEventListener("scroll", handleScroll);
    return () => scroller.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  // Mobile overflow menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  // Logout handler for mobile menu
  const handleLogout = () => {
    try {
      logout?.();
    } finally {
      navigate("/");
      // Ensure all persisted client state is cleared (redux/apollo caches, etc.)
      window.location.reload();
    }
  };

  const MOBILE_TOOLBAR_H = 56;
  const DESKTOP_TOOLBAR_H = 64;

  const careAiAssistantPhone = "tel:+18333681399";
  const topActionSx = {
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.8,
    px: 2.2,
    py: 0.86,
    borderRadius: "8px",
    border: `1px solid ${chrome.actionBorder || "rgba(255,255,255,0.6)"}`,
    backgroundColor: "transparent",
    color: chrome.actionText || "#f5f8fc",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    fontWeight: 700,
    fontSize: "0.78rem",
    lineHeight: 1,
    textDecoration: "none",
    transition: "all 150ms ease",
    "& .MuiSvgIcon-root": { color: chrome.actionText || "#f5f8fc", fontSize: 18 },
    "&:hover": {
      borderColor: chrome.actionHoverBg || "#ffffff",
      backgroundColor: chrome.actionHoverBg || "#ffffff",
      color: chrome.actionHoverText || "#22374a",
      "& .MuiSvgIcon-root": { color: chrome.actionHoverText || "#22374a" },
    },
  };

  return (
    <Fragment>
      <AppBar
        position="fixed"
        sx={{
          background: chrome.appBarBg || "#22374a",
          boxShadow: "none",
          transition: "1s ease-in-out all",
        }}
      >
        {tokenLoading && (
          <p style={{ margin: 0, padding: "4px 16px", fontSize: 12, opacity: 0.75 }}>
            Loading ...
          </p>
        )}

        {/* Row 1 */}
        <Toolbar
          disableGutters
          sx={{
            alignItems: "center",
            minHeight: { xs: MOBILE_TOOLBAR_H, sm: DESKTOP_TOOLBAR_H },
            pt: { xs: 1, sm: 2 },
            pb: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 2 },
            // push LEFT inward on sm+
            pl: { xs: 1, sm: 2 + LEFT_HIDDEN_TAB_OFFSET / 8 },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* LEFT: Logo + Brand */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              flexShrink: 0,
            }}
          >
            <StyledLink
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
            >
              <img
                src={Logo}
                alt="Company Logo"
                style={{
                  width: isMobile ? "24px" : "30px",
                  height: isMobile ? "24px" : "30px",
                  marginRight: isMobile ? "8px" : "16px",
                  marginBottom: "2px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = "0 0 10px rgba(255,255,255,0.6)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  fontWeight: 400,
                  fontFamily: "Roboto, Arial, sans-serif",
                  color: chrome.brandText || "#ffffff",
                  letterSpacing: "1px",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                <PlanyBrandMark />
              </Typography>
            </StyledLink>
          </Box>

          {/* RIGHT: ACTIONS */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 1,
              ml: "auto",
              flexShrink: 0,
            }}
          >
            {!isTimelyApp && <Tooltip title="Plany365 Care assistance for coordinating with care providers">
              <Button
                component="a"
                href={careAiAssistantPhone}
                size="small"
                variant="outlined"
                startIcon={<SupportAgentOutlinedIcon />}
                sx={topActionSx}
              >
                Call Plany Assist
              </Button>
            </Tooltip>}

            {/* Auth / Manage */}
            {!providerUser?.userId && (
              <StyledLink
                component={RouterLink}
                to={loginHref}
                sx={{
                  whiteSpace: "nowrap",
                  px: 1.5,
                  py: 0.75,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Log In / Sign Up
              </StyledLink>
            )}

            {providerUser?.userId ? (
              <StyledLink
                component={RouterLink}
                to={manageUrl}
                sx={{
                  ...topActionSx,
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <ManageAccountsIcon sx={{ fontSize: 18 }} />
                Manage Business
              </StyledLink>
            ) : (
              <Tooltip
                title={
                  <Typography sx={{ color: "#fff", fontSize: "0.875rem" }}>
                    Create a business profile using your business name (or your personal name if you
                    don’t have one) to manage services, events, classes, and related appointments.
                  </Typography>
                }
                arrow
                placement="bottom"
                componentsProps={{
                  tooltip: { sx: { backgroundColor: "#000", maxWidth: 300, p: "8px 12px" } },
                }}
              >
                <Box component="span" />
              </Tooltip>
            )}

            {/* Account — do NOT wrap in IconButton; it manages its own trigger */}
            {providerUser?.userId && <PositionedMenu menu="Customer" />}
          </Box>

          {/* MOBILE: overflow trigger (right) */}
          <Box sx={{ display: { xs: "flex", sm: "none" }, ml: "auto", alignItems: "center", gap: 0.5 }}>
            {providerUser?.userId ? (
              <Tooltip title="Manage your business" arrow>
                <Button
                  component={RouterLink}
                  to={manageUrl}
                  size="small"
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderColor: "rgba(255,255,255,0.55)",
                    color: "#eef4fb",
                    bgcolor: chrome.actionBg || "rgba(255,255,255,0.06)",
                    borderRadius: 999,
                    minHeight: 30,
                    px: 1.1,
                    py: 0.2,
                    fontSize: 12,
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.14)",
                    },
                  }}
                >
                  Manage Business
                </Button>
              </Tooltip>
            ) : (
              <Button
                component={RouterLink}
                to={loginHref}
                size="small"
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: "rgba(255,255,255,0.55)",
                  color: "#eef4fb",
                  bgcolor: chrome.actionBg || "rgba(255,255,255,0.06)",
                  borderRadius: 999,
                  minHeight: 30,
                  px: 1.1,
                  py: 0.2,
                  fontSize: 12,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.14)",
                  },
                }}
              >
                Log In
              </Button>
            )}
            <IconButton color="inherit" aria-label="more" onClick={openMenu} size="large">
              <MoreVertIcon />
            </IconButton>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              disableScrollLock
              PaperProps={{
                sx: (t) => ({
                  minWidth: 240,
                  zIndex: t.zIndex.modal + 1, // ensure above AppBar/second row
                }),
              }}
            >
              {!isTimelyApp && <>
              <MenuItem
                component="a"
                href={careAiAssistantPhone}
                onClick={closeMenu}
              >
                <ListItemIcon>
                  <SupportAgentOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Call Plany Assist"
                  secondary="Coordinate with care providers through Plany365"
                />
              </MenuItem>

              <Divider />
              </>}

              {!providerUser?.userId && (
                <MenuItem component={RouterLink} to={loginHref} onClick={closeMenu}>
                  <ListItemIcon>
                    <LoginIcon fontSize="small" />
                  </ListItemIcon>
                  Log In / Sign Up
                </MenuItem>
              )}

              {/* ✅ Show Logout only if logged in */}
              {providerUser?.userId && (
                <>
                  <Divider />
                  <MenuItem onClick={handleLogout} sx={{ padding: "6px 12px", fontSize: "14px" }}>
                    <ListItemText primary="🚪 Logout" sx={{ textAlign: "left" }} />
                  </MenuItem>
                </>
              )}

              <Divider />

              {/* RegionSelector inside a Menu: keep the parent menu open while interacting */}
              <MenuItem
                disableRipple
                disableTouchRipple
                disableGutters
                onClick={(e) => e.stopPropagation()}
                sx={{ cursor: "default", px: 2 }}
              >
                <ListItemIcon>
                  <PublicIcon fontSize="small" />
                </ListItemIcon>
                <Box sx={{ ml: 1 }}>
                  <RegionSelector />
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>

      </AppBar>

      {/* Offset below fixed header */}
      <Box aria-hidden sx={{ height: { xs: MOBILE_TOOLBAR_H, sm: 0 } }} />
    </Fragment>
  );
};

export default CustomerHeader;
